import {
  Box,
  Button,
  Container,
  DatePicker,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Table,
  TableProps,
  TimeInput
} from '@amzn/awsui-components-core';
import { FormikProps } from 'formik';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  FinanceCalendarEventEntity,
  FINANCE_CALENDAR_SCENARIOS,
  toFinanceCalendarWidgetData
} from '../../../widgets/finance-calendar-widget/FinanceCalendarData';
import { FinanceCalendarWidgetContent } from '../../../widgets/finance-calendar-widget/FinanceCalendarWidget';
import { HomePageWizard } from '../HomepageModel';

interface HomePageCalendarStepProps {
  formik: FormikProps<HomePageWizard>;
}

const scenarioOptions = FINANCE_CALENDAR_SCENARIOS.map((s) => ({ label: s, value: s }));

const HomePageCalendarStep: React.FC<HomePageCalendarStepProps> = ({ formik }) => {
  const events = formik.values.homePageDetailsAndWidgets.financeCalendarEvents || [];

  const [selectedEvents, setSelectedEvents] = useState<FinanceCalendarEventEntity[]>([]);
  const [editingEvent, setEditingEvent] = useState<FinanceCalendarEventEntity | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // New event form state
  const [newScenario, setNewScenario] = useState<string>(FINANCE_CALENDAR_SCENARIOS[0]);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('10:00:00');
  const [newEventName, setNewEventName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const resetForm = () => {
    setNewScenario(FINANCE_CALENDAR_SCENARIOS[0]);
    setNewDate('');
    setNewTime('10:00:00');
    setNewEventName('');
    setNewDescription('');
    setEditingEvent(null);
    setIsEditing(false);
  };

  const handleAddOrUpdateEvent = () => {
    if (!newScenario || !newDate || !newEventName) return;

    const dateTime = `${newDate}T${newTime || '10:00:00'}`;

    const event: FinanceCalendarEventEntity = {
      eventId: isEditing && editingEvent ? editingEvent.eventId : uuidv4(),
      scenario: newScenario,
      dateTime,
      event: newEventName,
      description: newDescription
    };

    let updatedEvents: FinanceCalendarEventEntity[];
    if (isEditing && editingEvent) {
      updatedEvents = events.map((e) => (e.eventId === editingEvent.eventId ? event : e));
    } else {
      updatedEvents = [...events, event];
    }

    formik.setFieldValue('homePageDetailsAndWidgets.financeCalendarEvents', updatedEvents);
    resetForm();
  };

  const handleEditEvent = () => {
    if (selectedEvents.length !== 1) return;
    const event = selectedEvents[0];
    setEditingEvent(event);
    setIsEditing(true);
    setNewScenario(event.scenario);
    // Parse dateTime into date and time parts
    const [datePart, timePart] = (event.dateTime || '').split('T');
    setNewDate(datePart || '');
    setNewTime(timePart || '10:00:00');
    setNewEventName(event.event);
    setNewDescription(event.description);
    setSelectedEvents([]);
  };

  const handleDeleteEvents = () => {
    const selectedIds = selectedEvents.map((e) => e.eventId);
    const updatedEvents = events.filter((e) => !selectedIds.includes(e.eventId));
    formik.setFieldValue('homePageDetailsAndWidgets.financeCalendarEvents', updatedEvents);
    setSelectedEvents([]);
    // Reset edit form if the event being edited was deleted
    if (isEditing && editingEvent && selectedIds.includes(editingEvent.eventId)) {
      resetForm();
    }
  };

  const handleMoveUp = () => {
    if (selectedEvents.length !== 1) return;
    const idx = events.findIndex((e) => e.eventId === selectedEvents[0].eventId);
    if (idx <= 0) return;
    const updated = [...events];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    formik.setFieldValue('homePageDetailsAndWidgets.financeCalendarEvents', updated);
  };

  const handleMoveDown = () => {
    if (selectedEvents.length !== 1) return;
    const idx = events.findIndex((e) => e.eventId === selectedEvents[0].eventId);
    if (idx < 0 || idx >= events.length - 1) return;
    const updated = [...events];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    formik.setFieldValue('homePageDetailsAndWidgets.financeCalendarEvents', updated);
  };

  const columnDefinitions: TableProps.ColumnDefinition<FinanceCalendarEventEntity>[] = [
    {
      id: 'scenario',
      header: 'Scenario',
      cell: (item) => item.scenario,
      sortingField: 'scenario',
      width: 120
    },
    {
      id: 'dateTime',
      header: 'Date/Time',
      cell: (item) => {
        const [datePart, timePart] = (item.dateTime || '').split('T');
        return `${datePart} ${timePart || ''}`;
      },
      sortingField: 'dateTime',
      width: 180
    },
    {
      id: 'event',
      header: 'Event',
      cell: (item) => item.event
    },
    {
      id: 'description',
      header: 'Description',
      cell: (item) => item.description
    }
  ];

  // Transform events to the widget's data format for preview
  const previewData = React.useMemo(() => {
    return toFinanceCalendarWidgetData(events);
  }, [events]);

  return (
    <SpaceBetween size="l" direction="vertical">
      {/* Add/Edit Event Form */}
      <Container
        header={
          <Header variant="h2" description="Add or edit finance calendar events displayed on the homepage">
            {isEditing ? 'Edit Calendar Event' : 'Add New Calendar Event'}
          </Header>
        }
      >
        <SpaceBetween size="m" direction="vertical">
          <FormField label="Scenario" description="Select the planning scenario for this event">
            <Select
              selectedOption={scenarioOptions.find((o) => o.value === newScenario) || null}
              onChange={({ detail }) => setNewScenario(detail.selectedOption.value || FINANCE_CALENDAR_SCENARIOS[0])}
              options={scenarioOptions}
              placeholder="Select scenario"
            />
          </FormField>
          <FormField label="Date" description="The date of the event">
            <DatePicker value={newDate} onChange={({ detail }) => setNewDate(detail.value)} placeholder="YYYY-MM-DD" />
          </FormField>
          <FormField label="Time" description="The time of the event (24h format)">
            <TimeInput value={newTime} onChange={({ detail }) => setNewTime(detail.value)} format="hh:mm:ss" placeholder="HH:MM:SS" />
          </FormField>
          <FormField label="Event Name" description="Short name for the event">
            <Input placeholder="e.g., 1st Pass: GP P&L Snapshot" value={newEventName} onChange={({ detail }) => setNewEventName(detail.value)} />
          </FormField>
          <FormField
            label={
              <>
                Description<i> - optional</i>
              </>
            }
            description="Detailed description of the event"
          >
            <Input
              placeholder="e.g., 1st Pass: GP P&L snapshot in Prophecy"
              value={newDescription}
              onChange={({ detail }) => setNewDescription(detail.value)}
            />
          </FormField>
          <SpaceBetween size="s" direction="horizontal">
            <Button variant="primary" onClick={handleAddOrUpdateEvent} disabled={!newScenario || !newDate || !newEventName}>
              {isEditing ? 'Update Event' : 'Add Event'}
            </Button>
            {isEditing && (
              <Button variant="link" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {/* Events Table */}
      <Table
        columnDefinitions={columnDefinitions}
        items={events}
        selectionType="multi"
        selectedItems={selectedEvents}
        onSelectionChange={({ detail }) => setSelectedEvents(detail.selectedItems)}
        trackBy="eventId"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No calendar events</b>
            <Box variant="p" color="inherit">
              Use the form above to add finance calendar events.
            </Box>
          </Box>
        }
        header={
          <Header
            variant="h2"
            counter={`(${events.length})`}
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={handleMoveUp} disabled={selectedEvents.length !== 1} iconName="angle-up">
                  Move up
                </Button>
                <Button onClick={handleMoveDown} disabled={selectedEvents.length !== 1} iconName="angle-down">
                  Move down
                </Button>
                <Button onClick={handleEditEvent} disabled={selectedEvents.length !== 1}>
                  Edit
                </Button>
                <Button onClick={handleDeleteEvents} disabled={selectedEvents.length === 0}>
                  Delete
                </Button>
              </SpaceBetween>
            }
          >
            Finance Calendar Events
          </Header>
        }
      />

      {/* Live Preview */}
      {previewData.length > 0 && (
        <Container header={<Header variant="h2">Preview</Header>}>
          <FinanceCalendarWidgetContent hasData="finished" data={previewData} />
        </Container>
      )}
    </SpaceBetween>
  );
};

export default HomePageCalendarStep;
